import { BarLayer, ComputedDatum, ResponsiveBar } from "@nivo/bar";
import { hexColorRegex } from "validations/matchers";
import { useState } from "react";
import { pSBC, truncateLongText } from "utilities/utilities";
import { BarsChartData } from "./ReportDowntimeTopFiveBarChartInterfaces";
import { useTheme } from "@mui/material";
import ReportPercentLayer from "./ReportPercentLayer.view";
import ReportSummaryLayer from "./ReportSummaryLayer";

const ReportDowntimeTopFiveBarChart = ({
    data,
    index,
    onBarClick,
    noPercent,
    selectedBar,
    useExternalSelectedBarProp,
}: BarsChartData) => {
    const themeStyles = useTheme();
    const colors: string[] = [];
    const keys: string[] = [];

    const theme = {
        axis: {
            fontSize: "12px",
            ticks: {
                text: {
                    fontSize: "10px",
                    fontFamily: "Source Sans 3",
                    fill: themeStyles.palette.text.primary,
                },
            },
        },
        tooltip: {
            container: {
                display: "none",
            },
        },
        legends: {
            text: {
                fontSize: 8,
                fill: themeStyles.palette.text.primary,
                outlineWidth: 0,
                outlineColor: "transparent",
                transform: "rotate(-20deg)",
            },
        },
    };

    const [localSelectedBar, setLocalSelectedBar] = useState<string | null>(null);

    const handleBarClick = (datum: ComputedDatum<{ [key: string]: string | number }> & { color: string }) => {
        if (!selectedBar && !useExternalSelectedBarProp) {
            setLocalSelectedBar(datum.id.toString());
        }
        onBarClick(datum.id.toString());
    };

    data.forEach((dataItem) => {
        return Object.values(dataItem).forEach((value) => {
            if (typeof value === "string" && hexColorRegex.test(value) && !colors.includes(value)) {
                colors.push(value);
            }
        });
    });

    data.forEach((dataItem) => {
        Object.keys(dataItem).forEach((key) => {
            if (key !== index && key !== "countryCode" && !keys.includes(key) && !key.includes("Color")) {
                keys.push(key);
            }
        });
    });

    const BarLayers = (noPercent ? ReportSummaryLayer : ReportPercentLayer) as unknown as BarLayer<{
        [key: string]: string | number;
    }>;

    const colorMappingWithLocalState: Record<string, string> = data.reduce((acc: Record<string, string>, item) => {
        if (!localSelectedBar) {
            acc[item.id] = colors[0];
            return acc;
        } else if (localSelectedBar === item.id) {
            acc[item.id] = colors[0];
            return acc;
        } else {
            acc[item.id] = pSBC(0.4, colors[0]) || colors[0];
            return acc;
        }
    }, {} as Record<string, string>);

    const colorMapping: Record<string, string> = data.reduce((acc: Record<string, string>, item) => {
        if (!selectedBar) {
            acc[item.id] = colors[0];
            return acc;
        } else if (selectedBar === item.id) {
            acc[item.id] = colors[0];
            return acc;
        } else {
            acc[item.id] = pSBC(0.4, colors[0]) || colors[0];
            return acc;
        }
    }, {} as Record<string, string>);

    return (
        <ResponsiveBar
            data={data}
            colors={(e) => (useExternalSelectedBarProp ? colorMapping[e.id] : colorMappingWithLocalState[e.id])}
            keys={keys}
            enableGridX
            theme={theme}
            enableGridY={false}
            margin={{ top: 10, right: 50, bottom: 10, left: 120 }}
            layout="horizontal"
            enableLabel={false}
            valueScale={{ type: "linear" }}
            indexScale={{ type: "band", round: true }}
            borderColor={{
                from: "color",
                modifiers: [["darker", 1.6]],
            }}
            onClick={handleBarClick}
            axisTop={null}
            axisRight={null}
            axisBottom={null}
            axisLeft={{
                format: (value) => `${truncateLongText(value.toString(), 20)}`,
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: "",
                legendPosition: "middle",
                legendOffset: -40,
            }}
            labelSkipWidth={12}
            labelSkipHeight={12}
            labelTextColor={{
                from: "color",
                modifiers: [["darker", 1.6]],
            }}
            legends={[]}
            role="application"
            padding={0.4}
            layers={["grid", "axes", "bars", "markers", "legends", BarLayers]}
            innerPadding={1}
        />
    );
};

export default ReportDowntimeTopFiveBarChart;
